import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, LogOut, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const SecuritySettings = () => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (form.next.length < 10) { toast.error('New password must be at least 10 characters'); return; }
    if (form.next !== form.confirm) { toast.error("New passwords don't match"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/change-password`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: form.current, new_password: form.next }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success('Password changed. Every other device has been signed out.');
        setForm({ current: '', next: '', confirm: '' });
      } else {
        toast.error(body.detail || 'Could not change password');
      }
    } finally {
      setSaving(false);
    }
  };

  const signOutEverywhere = async () => {
    if (!window.confirm('Sign out of the CRM on every device, including this one?')) return;
    setSigningOut(true);
    try {
      await fetch(`${API_URL}/api/auth/logout-everywhere`, { method: 'POST', credentials: 'include' });
    } finally {
      await logout();
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg"><KeyRound className="w-5 h-5 text-pink-500" /> Change password</CardTitle>
          <CardDescription>At least 10 characters. Changing it signs you out on every other device.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={changePassword} className="space-y-3 max-w-sm">
            <div>
              <Label htmlFor="pw-current">Current password</Label>
              <Input id="pw-current" type="password" autoComplete="current-password" value={form.current}
                onChange={(e) => setForm(p => ({ ...p, current: e.target.value }))} className="mt-1.5" />
              <p className="text-xs text-slate-500 mt-1">Leave blank if you only ever sign in with Google.</p>
            </div>
            <div>
              <Label htmlFor="pw-new">New password</Label>
              <Input id="pw-new" type="password" autoComplete="new-password" value={form.next}
                onChange={(e) => setForm(p => ({ ...p, next: e.target.value }))} className="mt-1.5" required minLength={10} />
            </div>
            <div>
              <Label htmlFor="pw-confirm">Confirm new password</Label>
              <Input id="pw-confirm" type="password" autoComplete="new-password" value={form.confirm}
                onChange={(e) => setForm(p => ({ ...p, confirm: e.target.value }))} className="mt-1.5" required minLength={10} />
            </div>
            <Button type="submit" disabled={saving} className="bg-gradient-to-r from-pink-400 to-rose-500 min-h-[44px]">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Change password
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg"><LogOut className="w-5 h-5 text-slate-500" /> Sign out everywhere</CardTitle>
          <CardDescription>Use this if you think someone else has access to your account, or you lost a device.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={signOutEverywhere} disabled={signingOut} className="min-h-[44px]">
            {signingOut && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Sign out on all devices
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default SecuritySettings;
